// ═══════════════════════════════════════════════════════════════════════
// Paramètres › Dispositifs sociaux › un règlement (pack de portée : dispositif entier, campagne ou type d'aide).
// Composition (règles et valeurs), saisie groupée des règles du gabarit encore absentes, simulation budgétaire sur
// hypothèses (conservée), soumission (cohérence bloquante, simulation jointe), validation EN BLOC par une autre
// personne (jamais l'auteur du règlement ni d'une de ses règles), refus motivé, annulation. Date d'effet jamais
// antérieure au jour : le serveur refuse (RETROACTIVITE_INTERDITE), son message est affiché.
// Routes : /api/configuration/packs… (circuit unique du Centre, DT-CONFIG-003).
// ═══════════════════════════════════════════════════════════════════════
import { useState } from 'react';
import { Plus, Send, Calculator, CheckCircle2, XCircle, Ban, ListChecks, FlaskConical } from 'lucide-react';
import { Carte, Bouton, BandeauInfo, Badge, Squelette, formaterDate } from '../../design/composants';
import { useChargement, Fenetre, Champ, PiedFenetre } from '../temps/reglagesCommun';
import { lireCfg, ecrireCfg, CONFIANCES, BadgeStatutRegle, FenetreValider, FenetreMotifCfg } from './configurationCommun';
import {
  SOURCES_DISPOSITIF, apiDs, ChampRegle, ErreurCfg, FormulaireHypotheses, ResultatBudget, aujourdHui, erreurCfg, hypothesesDe, reglablePour, toArray,
  valeurEnvoyee, valeurInitiale, valeurRemplie,
} from './dispositifsCommun';
import { FenetreVersionDispositif } from './VersionRegleDispositif';

export const estDemo = (p) => /\[DÉMO\]/.test(`${p?.libelle || ''} ${p?.code || ''}`);
const codeSuggere = (texte) => String(texte || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase().replace(/[^A-Z0-9_-]+/g, '-')
  .replace(/^-+|-+$/g, '').slice(0, 50);

/** Nouveau règlement pour la portée choisie (brouillon). */
export function FenetreNouveauReglement({ dispositif, portee, objet, onFermer, onTermine }) {
  const suffixe = objet ? (objet.numero || objet.code || '') : '';
  const [f, setF] = useState({
    code: codeSuggere(`REGLEMENT-${dispositif.code || ''}${suffixe ? `-${suffixe}` : ''}`), libelle: `Règlement — ${objet ? (objet.libelle || suffixe) : dispositif.libelle}`,
    date_debut: '', date_fin: '', source_nature: 'DECISION_BUREAU', source_reference: '', commentaire: '', motif: '',
  });
  const [erreur, setErreur] = useState(null);
  const maj = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const creer = async () => {
    setErreur(null);
    try {
      const p = await ecrireCfg('post', '/packs', {
        ...f, code: f.code.trim().toUpperCase(), date_fin: f.date_fin || null, niveau: 'SOGAS', domaines: ['DISPOSITIFS'],
        portee_dispositif_id: portee.dispositifId, portee_objet_type: portee.objetType || null, portee_objet_id: portee.objetId || null,
      });
      onTermine(p);
    } catch (e) { setErreur(erreurCfg(e)); }
  };
  return (
    <Fenetre titre="Nouveau règlement" Icone={ListChecks} onFermer={onFermer}>
      <ErreurCfg erreur={erreur} />
      <p style={{ margin: '0 0 12px', fontSize: 13 }}>Portée : <strong>{portee.libelle}</strong>. Le règlement regroupe les règles décidées ensemble ; il est simulé, soumis, puis validé en bloc par une autre personne.</p>
      <Champ libelle="Libellé" requis><input id="reglement-libelle" className="rh-champ-saisie" value={f.libelle} onChange={maj('libelle')} /></Champ>
      <Champ libelle="Identifiant" requis aide="Le même identifiant pour toutes les versions d'un même règlement (lettres majuscules, chiffres, tirets).">
        <input id="reglement-code" className="rh-champ-saisie" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} />
      </Champ>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
        <Champ libelle="Date d'effet" requis aide="Aujourd'hui ou plus tard : jamais d'effet rétroactif.">
          <input type="date" id="reglement-date" className="rh-champ-saisie" min={aujourdHui()} value={f.date_debut} onChange={maj('date_debut')} />
        </Champ>
        <Champ libelle="Date de fin" aide="Facultative."><input type="date" className="rh-champ-saisie" value={f.date_fin} onChange={maj('date_fin')} /></Champ>
      </div>
      <Champ libelle="Source de la décision" requis>
        <select id="reglement-source" className="rh-champ-saisie" value={f.source_nature} onChange={maj('source_nature')}>
          {Object.entries(SOURCES_DISPOSITIF).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </Champ>
      <Champ libelle="Pièce ou référence de la décision" aide="Exemple : procès-verbal du bureau (date, numéro)."><input id="reglement-reference" className="rh-champ-saisie" value={f.source_reference} onChange={maj('source_reference')} /></Champ>
      <Champ libelle="Commentaire"><textarea className="rh-champ-saisie" rows={2} value={f.commentaire} onChange={maj('commentaire')} /></Champ>
      <Champ libelle="Motif" requis><textarea id="reglement-motif" className="rh-champ-saisie" rows={2} value={f.motif} onChange={maj('motif')} /></Champ>
      <PiedFenetre>
        <Bouton onClick={onFermer}>Retour</Bouton>
        <Bouton variante="primaire" Icone={Plus} disabled={!f.code || f.libelle.trim().length < 3 || !f.date_debut || f.motif.trim().length < 3} onClick={creer}>Créer le règlement</Bouton>
      </PiedFenetre>
    </Fenetre>
  );
}

/** Saisie groupée des règles du gabarit encore absentes du règlement (une version par règle renseignée). */
function SaisieGroupee({ pack, regles, onTermine }) {
  const [valeurs, setValeurs] = useState(() => Object.fromEntries(regles.map((r) => [r.code, valeurInitiale(r)])));
  const [commun, setCommun] = useState({ source: pack.source_nature, reference_juridique: pack.source_reference || '', niveau_confiance: 'CONFIRME_SOGAS', motif: pack.motif || '' });
  const [enCours, setEnCours] = useState(false);
  const [echecs, setEchecs] = useState([]);
  const valeurDe = (r) => (valeurs[r.code] === undefined ? valeurInitiale(r) : valeurs[r.code]);
  const remplies = regles.filter((r) => valeurRemplie(r, valeurDe(r)));
  const maj = (k) => (e) => setCommun({ ...commun, [k]: e.target.value });
  const enregistrer = async () => {
    setEnCours(true); setEchecs([]);
    const ko = [];
    let ok = 0;
    for (const r of remplies) {
      try {
        // Une version par règle, dans l'ordre du gabarit : le serveur contrôle chaque valeur (bornes, liste fermée, forme).
        // eslint-disable-next-line no-await-in-loop
        await ecrireCfg('post', '/versions', {
          code: r.code, valeur: valeurEnvoyee(r, valeurDe(r)), pack_id: pack.id, niveau: 'SOGAS', source: commun.source,
          reference_juridique: commun.reference_juridique.trim() || null, niveau_confiance: commun.niveau_confiance, motif: commun.motif.trim(),
        });
        ok += 1;
      } catch (e) { ko.push({ code: r.code, libelle: r.libelle, ...erreurCfg(e) }); }
    }
    setEchecs(ko); setEnCours(false);
    onTermine(ok ? `${ok} règle(s) ajoutée(s) au règlement.${ko.length ? ` ${ko.length} refusée(s) : voir ci-dessous.` : ''}` : '');
  };
  const large = (r) => ['MATRICE', 'TEXTE', 'BAREME'].includes(r.type);
  return (
    <section aria-label="Saisie du règlement" data-saisie-groupee="">
      <h3 style={{ fontSize: 15, margin: '16px 0 4px' }}>Règles à saisir ({regles.length})</h3>
      <p className="rh-form-aide" style={{ margin: '0 0 10px' }}>Renseignez les valeurs décidées ; seules les règles renseignées sont ajoutées. Chaque règle prend la date d'effet du règlement ({formaterDate(pack.date_debut)}).</p>
      {echecs.length > 0 && (
        <BandeauInfo ton="danger" titre="Règles refusées par le serveur">
          <ul style={{ margin: 0, paddingLeft: 18 }}>{echecs.map((x) => <li key={x.code}>{x.libelle} : {x.message}{x.precisions.length ? ` (${x.precisions.join(' ; ')})` : ''}</li>)}</ul>
        </BandeauInfo>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', columnGap: 16 }}>
        {regles.map((r) => (
          <div key={r.code} style={large(r) ? { gridColumn: '1 / -1' } : undefined}>
            <ChampRegle r={r} valeur={valeurDe(r)} onChange={(v) => setValeurs((x) => ({ ...x, [r.code]: v }))} id={`saisie-${r.code}`} />
          </div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
        <Champ libelle="Source de la décision" requis>
          <select className="rh-champ-saisie" value={commun.source} onChange={maj('source')}>
            {Object.entries(SOURCES_DISPOSITIF).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </Champ>
        <Champ libelle="Niveau de confiance" requis>
          <select className="rh-champ-saisie" value={commun.niveau_confiance} onChange={maj('niveau_confiance')}>
            {Object.entries(CONFIANCES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </Champ>
      </div>
      <Champ libelle="Pièce ou référence de la décision"><input className="rh-champ-saisie" value={commun.reference_juridique} onChange={maj('reference_juridique')} /></Champ>
      <Champ libelle="Motif" requis><textarea id="saisie-motif" className="rh-champ-saisie" rows={2} value={commun.motif} onChange={maj('motif')} /></Champ>
      <Bouton variante="primaire" Icone={Plus} disabled={enCours || !remplies.length || commun.motif.trim().length < 3} onClick={enregistrer}>
        {enCours ? 'Enregistrement…' : `Ajouter ${remplies.length} règle(s) au règlement`}
      </Bouton>
    </section>
  );
}

function CompositionReglement({ pack, peutModifier, onModifier, onRetirer }) {
  const regles = toArray(pack.regles);
  if (!regles.length) return <p style={{ fontSize: 14 }}>Aucune règle dans ce règlement pour l'instant.</p>;
  return (
    <div className="rh-conteneur-tableau">
      <table className="rh-tableau" data-composition="">
        <thead><tr><th>Règle</th><th>Valeur</th><th>Statut</th>{peutModifier && <th aria-label="Actions" />}</tr></thead>
        <tbody>
          {regles.map((x) => (
            <tr key={x.id} data-code={x.code}>
              <td>{x.libelle}</td>
              <td style={{ maxWidth: 360 }}>{x.valeur}</td>
              <td><BadgeStatutRegle statut={x.statut} /></td>
              {peutModifier && (
                <td style={{ whiteSpace: 'nowrap' }}>
                  <Bouton petit onClick={() => onModifier(x)}>Modifier</Bouton>{' '}
                  <Bouton petit variante="discret" onClick={() => onRetirer(x)}>Retirer</Bouton>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Fiche d'un règlement (affichée sous la liste des règlements du dispositif). */
export function FicheReglement({ packId, dispositif, reglesGabarit, libellePack, moi, onFermer, onChange }) {
  const { donnees: pack, erreur, recharger } = useChargement(() => lireCfg(`/packs/${packId}`), [packId]);
  const [sous, setSous] = useState(null);
  const [message, setMessage] = useState('');
  const [erreurAction, setErreurAction] = useState(null);
  const [hypotheses, setHypotheses] = useState({});
  const [simulation, setSimulation] = useState(null);
  const [enCours, setEnCours] = useState(false);
  const apres = async (m) => { setSous(null); setErreurAction(null); if (m) setMessage(m); await recharger(); onChange(); };
  const agir = async (fn, m) => {
    setEnCours(true); setMessage(''); setErreurAction(null);
    try { await fn(); await apres(m); } catch (e) { setErreurAction(erreurCfg(e)); }
    setEnCours(false);
  };
  if (!pack) return <Carte titre="Règlement">{erreur ? <BandeauInfo ton="danger">{erreur}</BandeauInfo> : <Squelette hauteur={140} />}</Carte>;
  const portee = { dispositifId: pack.portee_dispositif_id, objetType: pack.portee_objet_type, objetId: pack.portee_objet_id, libelle: libellePack(pack) };
  const presents = new Set(toArray(pack.regles).map((x) => x.code));
  const aSaisir = reglesGabarit.filter((r) => reglablePour(r, pack.portee_objet_type) && !presents.has(r.code));
  const estAuteur = !!moi && (pack.auteur_id === moi || toArray(pack.regles).some((x) => x.auteur_id === moi));
  const statut = pack.statut_registre;
  const brouillon = statut === 'BROUILLON';
  const resultat = simulation || pack.simulation;
  const regleDe = (code) => reglesGabarit.find((r) => r.code === code);
  return (
    <Carte titre={`${pack.libelle} — version ${pack.version}`} Icone={ListChecks} action={<span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      {estDemo(pack) && <Badge ton="alerte">[DÉMO]</Badge>}<BadgeStatutRegle statut={pack.statut} /></span>} data-fiche-reglement={pack.id}>
      <p style={{ margin: '0 0 8px', fontSize: 14 }}>
        Portée : <strong>{portee.libelle}</strong> · à partir du {formaterDate(pack.date_debut)}{pack.date_fin ? ` jusqu'au ${formaterDate(pack.date_fin)}` : ''}
        {' · '}{SOURCES_DISPOSITIF[pack.source_nature] || pack.source_nature}{pack.source_reference ? ` (${pack.source_reference})` : ''}
      </p>
      <p style={{ margin: '0 0 8px', fontSize: 13, color: 'var(--sg-texte-2)' }}>
        Préparé par {pack.auteur || '—'}{pack.valideur ? ` · validé par ${pack.valideur}` : ''} · motif : {pack.motif}
        {pack.commentaire_validation ? ` · commentaire de validation : ${pack.commentaire_validation}` : ''}
      </p>
      {estDemo(pack) && <BandeauInfo ton="alerte" titre="[DÉMO] Règlement de démonstration">Valeurs d'exemple, sans valeur pour SOGAS : réservé à la base de test ou de recette. Jamais activé automatiquement.</BandeauInfo>}
      {message && <BandeauInfo ton="succes">{message}</BandeauInfo>}
      <ErreurCfg erreur={erreurAction} />
      {statut === 'A_VALIDER' && estAuteur && (
        <BandeauInfo ton="alerte" titre="Double regard">
          Vous avez préparé ce règlement ou l'une de ses règles : sa validation revient à une autre personne habilitée, qui relira les valeurs, la source, la date d'effet et la simulation.
        </BandeauInfo>
      )}
      {toArray(pack.activable?.raisons).length > 0 && (
        <BandeauInfo ton="alerte" titre="Points à régler avant validation">
          <ul style={{ margin: 0, paddingLeft: 18 }}>{pack.activable.raisons.map((x) => <li key={`${x.regle}-${x.message}`}>{x.message}</li>)}</ul>
        </BandeauInfo>
      )}

      <h3 style={{ fontSize: 15, margin: '12px 0 8px' }}>Composition ({toArray(pack.regles).length} règle(s))</h3>
      <CompositionReglement pack={pack} peutModifier={brouillon && pack.peut_preparer}
        onModifier={(x) => setSous({ type: 'modifier', version: x })} onRetirer={(x) => setSous({ type: 'retirer', version: x })} />
      {brouillon && pack.peut_preparer && aSaisir.length > 0 && (
        <SaisieGroupee pack={pack} regles={aSaisir} onTermine={apres} />
      )}
      {brouillon && pack.peut_preparer && aSaisir.length === 0 && <p style={{ fontSize: 14 }}>Toutes les règles du gabarit pour cette portée figurent dans le règlement.</p>}

      <h3 style={{ fontSize: 15, margin: '16px 0 8px', display: 'flex', alignItems: 'center', gap: 6 }}><Calculator size={16} aria-hidden="true" /> Simulation budgétaire</h3>
      {pack.peut_simuler && (
        <>
          <FormulaireHypotheses type={dispositif.type} valeur={hypotheses} onChange={setHypotheses} />
          <Bouton Icone={Calculator} disabled={enCours || !toArray(pack.regles).length} onClick={() => agir(async () => {
            setSimulation(await ecrireCfg('post', `/packs/${pack.id}/simuler`, { hypotheses: hypothesesDe(dispositif.type, hypotheses) }));
          }, 'Simulation calculée et conservée avec le règlement (aucune donnée modifiée).')}>Simuler le règlement</Bouton>
        </>
      )}
      <div style={{ marginTop: 12 }}>{resultat ? <ResultatBudget resultat={resultat} /> : <p style={{ fontSize: 14 }}>Pas encore simulé.</p>}</div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
        {brouillon && pack.peut_preparer && (
          <Bouton variante="primaire" Icone={Send} disabled={enCours || !toArray(pack.regles).length}
            onClick={() => agir(() => ecrireCfg('post', `/packs/${pack.id}/soumettre`), 'Règlement soumis à validation. Une autre personne habilitée doit le valider.')}>Soumettre à validation</Bouton>
        )}
        {statut === 'A_VALIDER' && pack.peut_valider && <Bouton variante="primaire" Icone={CheckCircle2} onClick={() => setSous({ type: 'valider' })}>Valider le règlement</Bouton>}
        {statut === 'A_VALIDER' && pack.peut_valider && <Bouton variante="danger" Icone={XCircle} onClick={() => setSous({ type: 'refuser' })}>Refuser</Bouton>}
        {(brouillon ? pack.peut_preparer : statut === 'A_VALIDER' && pack.peut_valider) && (
          <Bouton variante="discret" Icone={Ban} onClick={() => setSous({ type: 'annuler' })}>Annuler le règlement</Bouton>
        )}
        <Bouton variante="discret" onClick={onFermer}>Fermer</Bouton>
      </div>

      {sous?.type === 'modifier' && regleDe(sous.version.code) && (
        <FenetreVersionDispositif regle={regleDe(sous.version.code)} portee={portee} pack={pack} version={sous.version} onFermer={() => setSous(null)} onTermine={apres} />
      )}
      {sous?.type === 'retirer' && (
        <FenetreMotifCfg titre={`Retirer « ${sous.version.libelle} »`} aide="La règle est retirée du règlement en préparation ; la trace reste au journal." libelleBouton="Retirer" onFermer={() => setSous(null)}
          onConfirmer={async (motif) => { await ecrireCfg('post', `/packs/${pack.id}/regles/${sous.version.id}/retirer`, { motif }); await apres('Règle retirée du règlement.'); }} />
      )}
      {sous?.type === 'valider' && (
        <FenetreValider titre={`Valider : ${pack.libelle}`} resume={<ResultatBudget resultat={pack.simulation} />} onFermer={() => setSous(null)}
          onValider={async (commentaire) => {
            await ecrireCfg('post', `/packs/${pack.id}/valider`, { commentaire, confirmation: true });
            await apres('Règlement validé. Il s\'applique automatiquement à sa date d\'effet ; le règlement précédent de la même portée se ferme la veille.');
          }} />
      )}
      {sous?.type === 'refuser' && (
        <FenetreMotifCfg titre="Refuser le règlement" aide="Le règlement revient en préparation à son auteur, avec votre motif." onFermer={() => setSous(null)}
          onConfirmer={async (motif) => { await ecrireCfg('post', `/packs/${pack.id}/refuser`, { motif }); await apres('Règlement renvoyé en préparation.'); }} />
      )}
      {sous?.type === 'annuler' && (
        <FenetreMotifCfg titre="Annuler le règlement" aide="Le règlement et ses règles sont annulés ; ils restent visibles au journal." libelleBouton="Annuler le règlement" onFermer={() => setSous(null)}
          onConfirmer={async (motif) => { await ecrireCfg('post', `/packs/${pack.id}/annuler`, { motif }); await apres('Règlement annulé.'); }} />
      )}
    </Carte>
  );
}

/** Bouton du pack de DÉMONSTRATION (dispositif marqué démonstration ; refusé par le serveur hors base de test ou de recette). */
export function BoutonPackDemo({ dispositif, portee, onTermine, onErreur }) {
  const [enCours, setEnCours] = useState(false);
  if (!dispositif.donnees_demo || dispositif.gabarit !== 'CAMPAGNE_VENTE_SUBVENTIONNEE') return null;
  return (
    <Bouton Icone={FlaskConical} disabled={enCours} title="Réservé à la base de test ou de recette" style={{ whiteSpace: 'normal', maxWidth: '100%', textAlign: 'left' }}
      onClick={async () => {
        setEnCours(true);
        try {
          const p = await apiDs.packDemo(portee.dispositifId, { objetType: portee.objetType, objetId: portee.objetId });
          onTermine(p);
        } catch (e) { onErreur(erreurCfg(e)); }
        setEnCours(false);
      }}>[DÉMO] Préparer le règlement de démonstration</Bouton>
  );
}
